import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';

import CustomHttpClient from '../customHttpClient';

import {
  checkClaimsResponse,
  checkPoliciesResponse,
  getInsurancePolicyStates,
  getPossessionsType,
} from './utils';
import { getError } from '../../utils/getError';
import userHelper from '../../utils/userHelper';

import {
  InsurancePolicyList,
  GetInsurancePoliciesFilter,
  InsurancePolicy,
  ORDER_BY,
  ClaimsPerPolicyResponse,
  EndorsementsByPolicy,
  ElectronicPolicySuscriptionRequest,
  PreviousPDFSRequest,
  PreviousPDFSResponse,
  UserInformation,
  GetPoliciesSoonToExpireParams,
  GetPoliciesSoonToExpireServiceResponse,
  GetPoliciesExpiredAndNotRenewedParams,
  GetPoliciesExpiredAndNotRenewedServiceResponse,
  AlertsForProducersWeb,
} from '../../interfaces';
import { GetPoliciesGroupLifeNotUpdatedParams, GetPoliciesGroupLifeNotUpdatedServiceResponse } from '../../interfaces/services/get-policies-group-life-not-updated-service.interface';
import { GetPoliciesGroupLifeLTCParams, GetPoliciesGroupLifeLTCServiceResponse } from '../../interfaces/services/get-policies-group-life-ltc-service.interface';
import { GetPoliciesLastEndorsementReversalParams, GetPoliciesLastEndorsementReversalServiceResponse } from '../../interfaces/services/get-policies-last-endorsement-reversal-service.interface';
import { RevertEndorsementRequest } from '../../interfaces/services/revert-endorsement-request';
import { GetPoliciesPendingRenewelDueToBalanceParams, GetPoliciesPendingRenewelDueToBalanceServiceResponse } from '../../interfaces/services/get-policies-pending-renewel-due-to-balance-service';
import { GetPaymentsTypesRequest, GetPaymentsTypesResponse, PostChangePaymentTypeRequest } from '../../interfaces/services/change-payment-type-service';
import { InformFeePaidRequest } from '../../interfaces/services/inform-fee-paid-request';
import { GetWithdrawalRequestParams, GetWithdrawalRequestServiceResponse } from '../../interfaces/services/get-withdrawal-request-service';
import { GetPoliciesOfXlsx } from '../../interfaces/services/get-policies-of-xlsx';
import { QUICKFILTER } from '../../interfaces/enums/quick-filter-enum';
import { TotalPolicyBalance } from '../../interfaces/services/get-total-policy-balance-service';


const getUserInfo = () => {
  const {
    codigoDeEmpresa,
    codigoDeSucursal,
    intermediarioPrincipal,
    intermediarioSecundario,
  }: UserInformation = userHelper.getUser()!;

  return {
    empresa: codigoDeEmpresa,
    sucursal: codigoDeSucursal,
    nivt: intermediarioSecundario?.nivelInter,
    nivc: intermediarioSecundario?.codigoInter,
    nit1: intermediarioPrincipal?.nivelInter,
    niv1: intermediarioPrincipal?.codigoInter,
  };
};

const customHttpClient = CustomHttpClient({ baseUrl: 'productores' });
const customHttpClientPolicy = CustomHttpClient({ baseUrl: 'polizas', scope: 'Polizas' });

const InsurancePolicyService = {
  async getInsurancePolicies(
    filters: GetInsurancePoliciesFilter
  ): Promise<AxiosResponse<InsurancePolicyList, any>> {
    let params = {};
    try {
      let url = 'listadoDePolizas';
      /** Solo para el caso particular de buscar una poliza */
      /* Si se busca por póliza se debe tomar el número de póliza y el primer ítem del select de rama */
      const formattedDates = filters.date.map((date) =>
        dayjs(date).format('YYYY-MM-DD')
      );
      if (filters.policyNumber && filters.branch && filters.branch.length > 0) {
        url = 'buscarPoliza';
        params = {
          ...getUserInfo(),
          rama: filters.branch[0],
          poliza: filters.policyNumber,
        }
        
      } else if (filters.insured && filters.insured?.length > 0) {
        url = 'buscarPolizaPorAsegurado';
        params = {
          ...getUserInfo(),
          codigoDeAsegurado: filters.insured[0]
        }
      } else if (filters.licensePlate && filters.licensePlate?.length > 0) {
        url = 'listadoPolizasPorPatente';
        params = {
          ...getUserInfo(),
          patente: filters.licensePlate
        }
      } else {
        params = {
          ...getUserInfo(),
          fechaDesde: formattedDates[0],
          fechaHasta: formattedDates[1],
        }
      }
      const preResponse = await customHttpClient.get(url, { params });
      const response = checkPoliciesResponse(preResponse);
      /* Termina ese caso */


      /** Fitros superiores, todos aplicados client side */
      response.data.polizas = response.data
        .polizas!.map((p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        })
        .filter(
          (poliza: InsurancePolicy) =>
            (filters.branch?.length === 0 ||
              filters.branch?.includes(poliza.rama)) &&
            (filters.producer?.length === 0 ||
              filters.producer?.includes(poliza.codigoProductor)) &&
            (filters.state?.length === 0 ||
              poliza.estados?.some((state) => filters.state?.includes(state)))
        )
        .sort((a: InsurancePolicy, b: InsurancePolicy) => {
          if (filters.orderBy === ORDER_BY.ALPHABETICAL_ORDER)
            return a.nombreAsegurado.localeCompare(b.nombreAsegurado);
          if (filters.orderBy === ORDER_BY.OLDEST_FIRST)
            return dayjs(a.fechaEmision).diff(dayjs(b.fechaEmision));
          if (filters.orderBy === ORDER_BY.MOST_RECENT_FIRST)
            return dayjs(b.fechaEmision).diff(dayjs(a.fechaEmision));
          return dayjs(b.fechaEmision).diff(dayjs(a.fechaEmision));
        });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getInsurancePolicyById(
    insurancePolicyId: string,
    branch: string
  ): Promise<AxiosResponse<InsurancePolicyList, any>> {
    const url = 'buscarPoliza';
    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: insurancePolicyId,
    };

    try {
      const response = await customHttpClient.get<InsurancePolicyList>(url, {
        params,
      });

      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
            possessionsType: getPossessionsType(p.grupoDeRama),
          };
        }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesSoonToExpire(): Promise<
    AxiosResponse<GetPoliciesSoonToExpireServiceResponse, any>
  > {
    const url = 'polizasProximasAVencer';

    const toDate = dayjs().add(QUICKFILTER.MAX_RANGE, 'days').format(QUICKFILTER.DATE_FORMAT);
    const fromDate = dayjs().format(QUICKFILTER.DATE_FORMAT);

    const params: GetPoliciesSoonToExpireParams = {
      ...getUserInfo(),
      fechaDesde: fromDate,
      fechaHasta: toDate,
    };

    try {
      const preResponse =
        await customHttpClient.get<GetPoliciesSoonToExpireServiceResponse>(
          url,
          { params }
        );
      const response = checkPoliciesResponse(preResponse);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesOfXlsx(filters: GetInsurancePoliciesFilter): Promise<
    AxiosResponse<GetPoliciesOfXlsx, any>
  > {
    const url = 'exportarListadoDePolizas';

    const formattedDates = filters.date.map((date) =>
      dayjs(date).format(QUICKFILTER.DATE_FORMAT)
    );

    const params: GetPoliciesSoonToExpireParams = {
      ...getUserInfo(),
      fechaDesde: formattedDates[0],
      fechaHasta: formattedDates[1],
    };

    try {
      const preResponse =
        await customHttpClient.get<GetPoliciesOfXlsx>(
          url,
          { params }
        );

      return preResponse;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesGroupLifeNotUpdated(): Promise<
    AxiosResponse<GetPoliciesGroupLifeNotUpdatedServiceResponse, any>
  > {
    const url = 'vidaColectivoSinActualizar';

    const toDate = dayjs().add(QUICKFILTER.MAX_RANGE, 'days').format(QUICKFILTER.DATE_FORMAT);
    const fromDate = dayjs().format(QUICKFILTER.DATE_FORMAT);

    const params: GetPoliciesGroupLifeNotUpdatedParams = {
      ...getUserInfo(),
      fechaDesde: fromDate,
      fechaHasta: toDate,
    };

    try {
      let response =
        await customHttpClient.get<GetPoliciesSoonToExpireServiceResponse>(
          url,
          { params }
        );
      response = checkPoliciesResponse(response);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesGroupLifeLTC(): Promise<
    AxiosResponse<GetPoliciesGroupLifeLTCServiceResponse, any>
  > {
    const url = 'vidaLctSinActualizar';

    const toDate = dayjs().add(QUICKFILTER.MAX_RANGE, 'days').format(QUICKFILTER.DATE_FORMAT);
    const fromDate = dayjs().format(QUICKFILTER.DATE_FORMAT);

    const params: GetPoliciesGroupLifeLTCParams = {
      ...getUserInfo(),
      fechaDesde: fromDate,
      fechaHasta: toDate,
    };

    try {
      let response =
        await customHttpClient.get<GetPoliciesGroupLifeLTCServiceResponse>(
          url,
          { params }
        );
      response = checkPoliciesResponse(response);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesPendingRenewalDueToBalance(): Promise<
    AxiosResponse<GetPoliciesPendingRenewelDueToBalanceServiceResponse, any>
  > {
    const url = 'pendientesDeRenovacionPorSaldo';

    const toDate = dayjs().add(QUICKFILTER.MAX_RANGE, 'days').format(QUICKFILTER.DATE_FORMAT);
    const fromDate = dayjs().format(QUICKFILTER.DATE_FORMAT);

    const params: GetPoliciesPendingRenewelDueToBalanceParams = {
      ...getUserInfo(),
      fechaDesde: fromDate,
      fechaHasta: toDate,
    };

    try {
      let response =
        await customHttpClient.get<GetPoliciesPendingRenewelDueToBalanceServiceResponse>(
          url,
          { params }
        );
      response = checkPoliciesResponse(response);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getAlertsForInsurancePolicyList(): Promise<AxiosResponse<AlertsForProducersWeb>> {
    {
      const url = 'obtenerAlertas';
      const params = { ...getUserInfo() };
      try {
        const response = await customHttpClient.get<AlertsForProducersWeb>(url, { params });
        return response;
      } catch (error) {
        throw new Error(getError(error));
      }
    }
  },

  async getClaimsPerPolicy(
    insurancePolicyId: string,
    branch: string
  ): Promise<AxiosResponse<ClaimsPerPolicyResponse, any>> {
    const url = 'siniestrosPorPoliza';
    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: insurancePolicyId,
    };

    try {
      const response = await customHttpClient.get<ClaimsPerPolicyResponse>(
        url,
        { params }
      );

      return checkClaimsResponse(response);
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPolicyEndorsements(
    insurancePolicyId: string,
    branch: string
  ): Promise<AxiosResponse<EndorsementsByPolicy, any>> {
    const url = 'endososDeUnaPoliza';

    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: insurancePolicyId,
    };

    try {
      const response = await customHttpClient.get<EndorsementsByPolicy>(url, {
        params,
      });
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getTotalPolicyBalance(
    insurancePolicyId: string,
    branch: string,
    article: string,
    superpolicy: string
  ): Promise<AxiosResponse<TotalPolicyBalance, any>> {
    const url = 'saldoDeUnaPoliza';

    const params = {
      ...getUserInfo(),
      rama: branch,
      nroPoliza: insurancePolicyId,
      articulo: article,
      superpoliza: superpolicy
    };

    try {
      const response = await customHttpClientPolicy.get<TotalPolicyBalance>(url, {
        params,
      });
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async suscribeOnElectronicPolicy(
    electronicPolicySuscription: ElectronicPolicySuscriptionRequest
  ): Promise<AxiosResponse<{ resultado: string }, any>> {
    const url = 'suscripcionPolizaElectronica';
    const params = {
      ...getUserInfo(),
      ...electronicPolicySuscription,
    };

    try {
      const response = await customHttpClient.post<{ resultado: string }>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postRevertEndorsement(
    revertEndorsement: RevertEndorsementRequest
  ): Promise<AxiosResponse<{ resultado: string }, any>> {
    const url = 'endosos/ejecutarEndosoReversion';
    const params = {
      base: getUserInfo(),
      ...revertEndorsement
    };

    try {
      const response = await customHttpClient.post<{ resultado: string }>(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPaymentsTypes(request: GetPaymentsTypesRequest): Promise<GetPaymentsTypesResponse> {
    const url = 'obtenerCambioFormaDePagoWeb';

    const params = {
      ...getUserInfo(),
      ...request
    };

    try {
      const response = await customHttpClient.get<GetPaymentsTypesResponse>(url, {
        params,
      });
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postChangePaymentFormat(
    changePaymentType: PostChangePaymentTypeRequest
  ): Promise<AxiosResponse> {
    const url = 'solicitudCambioFormaDePagoWeb';
    const params = {
      ...getUserInfo(),
      ...changePaymentType
    };

    try {
      const response = await customHttpClient.post(
        url,
        params
      );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPreviousPDFS(
    request: PreviousPDFSRequest
  ): Promise<AxiosResponse<PreviousPDFSResponse, any>> {
    const url = 'pdfsAnteriores';
    const params = { ...getUserInfo(), ...request };

    try {
      const response = await customHttpClient.get<PreviousPDFSResponse>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getPoliciesExpiredAndNotRenewed(): Promise<AxiosResponse<GetPoliciesExpiredAndNotRenewedServiceResponse, any>> {
    const url = 'vencidasNoRenovadas';
    const params: GetPoliciesExpiredAndNotRenewedParams = {
      ...getUserInfo(),
    };

    try {
      const preResponse =
        await customHttpClient.get<GetPoliciesExpiredAndNotRenewedServiceResponse>(
          url,
          { params }
        );
      const response = checkPoliciesResponse(preResponse);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPoliciesLastEndorsementReversal(): Promise<AxiosResponse<GetPoliciesLastEndorsementReversalServiceResponse, any>> {
    const url = 'endosos/listaEndososParaRevertir';
    const params: GetPoliciesLastEndorsementReversalParams = {
      ...getUserInfo(),
    };

    try {
      const preResponse =
        await customHttpClient.get<GetPoliciesLastEndorsementReversalServiceResponse>(
          url,
          { params }
        );
      const response = checkPoliciesResponse(preResponse);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async getWithdrawalRequest(): Promise<AxiosResponse<GetWithdrawalRequestServiceResponse, any>> {
    const url = 'solicitudesDeArrepentimiento';
    const params: GetWithdrawalRequestParams = {
      ...getUserInfo(),
    };

    try {
      const preResponse = await doGetRequest<GetWithdrawalRequestServiceResponse>(url, params);
      const response = checkPoliciesResponse(preResponse);
      response.data.polizas = response.data.polizas!.map(
        (p: InsurancePolicy) => {
          return {
            ...p,
            estados: getInsurancePolicyStates(p),
          };
        }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async informReportFeePaidDocument(
    requestParams: InformFeePaidRequest
  ): Promise<AxiosResponse<string>> {
    const url = 'informaCuotaPagada';

    try {
      const response =
        await customHttpClient.post<string>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  }
};

async function doGetRequest<T>(
  url: string,
  params: Record<string, any>
): Promise<AxiosResponse<T>> {
  return customHttpClient.get<T>(url, { params });
};

export default InsurancePolicyService;
